// Client-side registration for uncaught error + unhandled rejection reporting
// to /api/public/hooks/report-client-error. Deduped in-session, best-effort,
// never throws. Import from __root.tsx (inside a useEffect) so it only runs in
// the browser and doesn't affect SSR.

const REPORTED = new Map<string, number>();
const REPORT_TTL_MS = 30_000;
let registered = false;

function send(error: unknown) {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  try {
    const err = error as Error | undefined;
    const message = String(err?.message ?? err ?? "unknown").slice(0, 500);
    if (!message || message === "unknown") return;
    if (/^ResizeObserver|^Script error\.?$|AbortError|Load failed$/i.test(message)) return;
    const stackFirst = (err?.stack ?? "").split("\n").slice(0, 1).join("");
    const dedupeKey = `${message}::${stackFirst}`;
    const now = Date.now();
    for (const [k, at] of REPORTED) if (now - at > REPORT_TTL_MS) REPORTED.delete(k);
    if (REPORTED.has(dedupeKey)) return;
    REPORTED.set(dedupeKey, now);

    const payload = JSON.stringify({
      message,
      stack: err?.stack?.slice(0, 2000),
      route: window.location.pathname + window.location.search,
      ua: navigator.userAgent,
    });
    const url = "/api/public/hooks/report-client-error";
    const nav = navigator as Navigator & { sendBeacon?: (u: string, b: Blob) => boolean };
    if (typeof nav.sendBeacon === "function") {
      const blob = new Blob([payload], { type: "application/json" });
      if (nav.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch { /* never propagate */ }
}

export function registerClientErrorReporter(): void {
  if (registered || typeof window === "undefined") return;
  registered = true;
  window.addEventListener("error", (event) => send((event as ErrorEvent).error ?? event.message));
  window.addEventListener("unhandledrejection", (event) => send((event as PromiseRejectionEvent).reason));
}